import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { clsx } from 'clsx'
import Fuse from 'fuse.js'

import { SearchIcon } from '@dfds-frontend/navigator-icons/icons'
import { TextField } from '@dfds-pax/shared-components/client'

import { ActiveSelectField, useActiveSelectField } from '../../context/ActiveSelectFieldProvider'
import { handleListNavigation } from '../../Utils/list-keyboard'

import { Category, Route } from '../../Hooks/routes/route-types'

interface Props {
  onEscape?: () => void
  onSelectRoute: (route: Route, restoreFocus: boolean) => void
  routeCategories: Category[]
  selectedRoute?: Route
  travelSearchRef: React.RefObject<HTMLDivElement>
}

const DESKTOP_MIN_WIDTH = 1024 // tailwind `lg`, where the travel search becomes a fixed bar
const MOBILE_STICKY_OFFSET = -350

const RoutesWithSearch: React.FC<Props> = ({ onEscape, onSelectRoute, routeCategories, selectedRoute, travelSearchRef }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredCategories, setFilteredCategories] = useState<Category[]>(routeCategories)
  const [maxHeight, setMaxHeight] = useState(1000) // Default height
  const { activeField, closeIfActive, setTravelSearchInViewport } = useActiveSelectField()
  const [isScrollable, setIsScrollable] = useState<boolean>(false)

  const { t } = useTranslation()

  const divRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const adjustHeight = () => {
    if (divRef.current) {
      const rect = divRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      if (isScrollable && rect.bottom >= viewportHeight) {
        setMaxHeight(viewportHeight - rect.top)
      } else {
        setMaxHeight(1000)
      }
    }
  }

  useEffect(() => {
    adjustHeight()
    window.addEventListener('resize', adjustHeight)
    return () => {
      window.removeEventListener('resize', adjustHeight)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (inputRef.current && activeField === ActiveSelectField.ROUTE) {
      inputRef.current.focus()
    } else if (activeField !== ActiveSelectField.ROUTE) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSearchTerm('')
      setFilteredCategories(routeCategories)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeField])

  // The auto focus above opens the soft keyboard on touch devices; dismiss it once the search
  // field is scrolled out of sight so the keyboard stops covering the route list.
  useEffect(() => {
    const input = inputRef.current
    const container = divRef.current
    if (!input || !container || activeField !== ActiveSelectField.ROUTE) return
    if (window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`).matches) return

    let hasBeenVisible = false
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          hasBeenVisible = true
          return
        }
        // The observer also reports "not intersecting" before the panel has been laid out.
        if (!hasBeenVisible || document.activeElement !== input) return
        input.blur()
        // Park focus on the panel so Escape still closes it instead of focus falling to <body>.
        container.focus({ preventScroll: true })
      },
      { threshold: 0 }
    )

    observer.observe(input)
    return () => observer.disconnect()
  }, [activeField])

  useEffect(() => {
    adjustHeight()
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/refs
  }, [divRef.current?.offsetHeight, divRef.current?.offsetTop, activeField, isScrollable])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFilteredCategories(routeCategories)
  }, [routeCategories])

  useEffect(() => {
    // Desktop sticks as soon as the container's bottom passes the viewport top, mobile keeps an extra scroll buffer.
    const stickyThreshold = () => (window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`).matches ? 0 : MOBILE_STICKY_OFFSET)

    const onScroll = () => {
      const rect = travelSearchRef.current?.getBoundingClientRect()

      if (rect) {
        const threshold = stickyThreshold()

        if (!isScrollable && rect.bottom < threshold) {
          setIsScrollable(true)
          closeIfActive(ActiveSelectField.ROUTE)
        } else if (rect.bottom >= 0) {
          setIsScrollable(false)
        }

        setTravelSearchInViewport(rect.bottom < threshold)
      }
    }

    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScrollable, activeField])

  const flatRoutes = useMemo(() => {
    return routeCategories.flatMap((category) =>
      category.routes.map((route) => ({
        categoryName: category.name,
        routeCode: route.routeCode,
        routeName: route.name,
      }))
    )
  }, [routeCategories])

  const fuseCategories = useMemo(
    () =>
      new Fuse(routeCategories, {
        distance: 100, //Added to allow for partial matches
        keys: ['name'],
        threshold: 0.4, //Added to allow for partial matches
      }),
    [routeCategories]
  )

  const fuse = useMemo(
    () =>
      new Fuse(flatRoutes, {
        distance: 80, //Added to allow for partial matches
        keys: ['routeName', 'routeCode'],
        threshold: 0.4, //Added to allow for partial matches
      }),
    [flatRoutes]
  )

  const handleSearch = (e: React.ChangeEvent) => {
    const term = (e.target as HTMLInputElement).value
    setSearchTerm(term)

    if (term.trim() === '') {
      setFilteredCategories(routeCategories)
      return
    }

    const categoryResults = fuseCategories.search(term)
    const results = fuse.search(term)
    const matchingRouteCodes = new Set(results.map((result) => result.item.routeCode))
    const filtered = routeCategories
      .map((category) => {
        const matchedRoutes = category.routes.filter(
          (route) => matchingRouteCodes.has(route.routeCode) || categoryResults.find((catRes) => catRes.item.name === category.name)
        )

        return {
          ...category,
          routes: matchedRoutes,
        }
      })
      .filter((category) => category.routes.length > 0) // Remove categories with no matching routes

    setFilteredCategories(filtered)
  }

  // Escape is closed globally by ActiveSelectFieldProvider, so focus must be restored locally to avoid it landing on <body>
  const handleContainerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') onEscape?.()
  }

  const resultCount = filteredCategories.reduce((count, category) => count + category.routes.length, 0)

  // Roving tabindex: the list is a single tab stop anchored on the selected route if visible, or the first when none is selected.
  const rovingRouteCode = filteredCategories.some((c) => c.routes.some((r) => r.routeCode === selectedRoute?.routeCode))
    ? selectedRoute?.routeCode
    : filteredCategories[0]?.routes[0]?.routeCode

  return (
    <div
      className={clsx(
        'bg-semantic-global-background-white',
        'lg:w-[calc(100vw_-_144px)] lg:p-semantic-global-md xl:w-[calc(100vw_-_33px)] xl:max-w-[1137px]',
        'relative flex flex-col gap-3 overflow-auto pb-semantic-global-lg focus:outline-none',
        'scrollbar-none lg:scrollbar-thin lg:scrollbar-track-transparent lg:scrollbar-thumb-dfds-text-dark-disabled'
      )}
      onKeyDown={handleContainerKeyDown}
      ref={divRef}
      style={{ maxHeight: `${window.innerWidth > 1023 && maxHeight}px` }}
      tabIndex={-1}
    >
      <div className="sticky top-0 box-border w-full bg-semantic-global-background-white px-semantic-global-md pt-semantic-global-md md:relative md:w-[400px]">
        <TextField
          label={t('ROUTE')}
          iconPosition="after"
          icon={<SearchIcon className="text-semantic-global-text-default" size="md" />}
          id="routeSearch"
          name="routeSearch"
          inputType="text"
          onChange={handleSearch}
          placeholder={t('SEARCH-FOR-ROUTE-PLACEHOLDER')}
          ref={inputRef}
          value={searchTerm}
        />
      </div>
      <span className="sr-only" aria-live="polite">
        {searchTerm ? `${resultCount} route${resultCount === 1 ? '' : 's'} found` : ''}
      </span>
      <div
        aria-label="Routes"
        role="listbox"
        data-list
        className="m-0 border-0 border-solid p-0 lg:columns-4 max-md:columns-1 md:max-lg:columns-3"
      >
        {filteredCategories
          ? filteredCategories.map((routeCategory) => {
              const categoryLabelId = `route-category-${routeCategory?.name?.replace(/\s+/g, '-')}`
              return (
                <div className="break-inside-avoid" data-e2e="route-category" key={routeCategory?.name}>
                  <div className="flex h-[70px] items-center border-0 border-b border-solid border-semantic-global-border-light border-b-semantic-global-border-light pl-semantic-global-md md:border-none lg:h-[50px]">
                    <div
                      className="mt-semantic-global-md text-base font-medium text-semantic-global-text-default lg:mb-0 lg:ml-0"
                      id={categoryLabelId}
                    >
                      {routeCategory?.name}
                    </div>
                  </div>
                  <div role="group" aria-labelledby={categoryLabelId} className="m-0 p-0">
                    {routeCategory.routes.map((route) => (
                      <div
                        role="option"
                        data-list-option
                        aria-selected={route.routeCode === selectedRoute?.routeCode}
                        tabIndex={route.routeCode === rovingRouteCode ? 0 : -1}
                        onClick={(event) => onSelectRoute(route, event.detail === 0)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            onSelectRoute(route, event.detail === 0)
                            return
                          }
                          handleListNavigation(event)
                        }}
                        className={clsx(
                          'relative m-0 flex w-full cursor-pointer list-none items-center border-0 border-b border-solid border-semantic-global-border-light bg-transparent text-left',
                          'focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-semantic-global-primary-default',
                          // Mobile rows need a comfortable touch target, from md the list is a multi-column desktop layout
                          'min-h-[50px] py-semantic-global-sm pl-semantic-global-md pr-semantic-global-md',
                          'md:h-[50px] md:min-h-0 md:border-none md:py-0 md:pr-0 lg:h-[40px]'
                        )}
                        key={route.routeCode}
                      >
                        <div data-e2e={route.routeCode} data-e2e-name="route-names">
                          <span className="text-base font-normal text-semantic-global-primary-default hover:text-semantic-global-primary-dark">
                            {route.name?.replaceAll('–', ' - ').replaceAll('-', ' - ')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          : null}
      </div>
    </div>
  )
}

export default RoutesWithSearch
